<script>
  import { onDestroy, onMount, tick } from 'svelte';
  import {
    Camera,
    CameraOff,
    ChevronUp,
    Expand,
    Grid2X2,
    Maximize2,
    Mic,
    MicOff,
    Minimize2,
    Move,
    MonitorUp,
    PhoneOff,
    PictureInPicture2,
    RefreshCw,
    Repeat2,
    Settings,
    Shrink,
    UserRound
  } from 'lucide-svelte';
  import { formatDurations } from '$lib/utils/datetime.js';
  import { acceptCall } from '$lib/websocket-client.js';
  import { closeActiveCall, showCallNotice, updateActiveCall } from './call-store.js';
  import { createWebRTCSession } from './webrtc-session.js';
  import CallNetworkDiagnostics from './CallNetworkDiagnostics.svelte';
  import CallToolbarSettings from './CallToolbarSettings.svelte';
  import {
    CALL_TOOLBAR_ACTIONS,
    DEFAULT_CALL_TOOLBAR_CONFIG,
    loadCallToolbarConfig,
    normalizeCallToolbarConfig,
    resetCallToolbarConfig,
    saveCallToolbarConfig,
    isCallToolbarConfigEqual
  } from './call-toolbar-config.js';

  export let call;
  export let mode = 'video';
  export let onClose = null;

  let root;
  let localVideo;
  let remoteVideo;
  let remoteAudio;
  let videoStage;
  let localStream;
  let remoteStream;
  let session;
  let devices = [];
  let selectedCamera = '';
  let selectedMicrophone = '';
  let selectedSpeaker = '';
  let status = call?.status || 'connecting';
  let elapsed = 0;
  let timer = null;
  let micOn = true;
  let cameraOn = mode === 'video';
  let screenSharing = false;
  let remoteScreenSharing = false;
  let minimized = false;
  let expanded = mode === 'video';
  let gridLayout = false;
  let showSettings = false;
  let settingsTab = 'devices';
  let toolbarConfig = normalizeCallToolbarConfig(DEFAULT_CALL_TOOLBAR_CONFIG);
  let savedToolbarConfig = normalizeCallToolbarConfig(DEFAULT_CALL_TOOLBAR_CONFIG);
  let savingToolbar = false;
  let refreshingDevices = false;
  let zoom = 1;
  let canShareScreen = false;
  let swappedFrames = false;
  let localPosition = null;
  let dragState = null;

  $: title = call?.title || call?.fromName || 'Cuộc gọi';
  $: statusText = getStatusText(status);
  $: cameraDevices = devices.filter(({ kind }) => kind === 'videoinput');
  $: microphoneDevices = devices.filter(({ kind }) => kind === 'audioinput');
  $: speakerDevices = devices.filter(({ kind }) => kind === 'audiooutput');
  $: visibleToolbarActions = toolbarConfig.actions
    .filter(({ visible }) => visible)
    .map((item) => ({ ...item, ...CALL_TOOLBAR_ACTIONS.find(({ id }) => id === item.id) }))
    .filter(({ modes }) => modes?.includes(mode));
  $: hasToolbarChanges = !isCallToolbarConfigEqual(toolbarConfig, savedToolbarConfig);

  onMount(async () => {
    canShareScreen = Boolean(navigator.mediaDevices?.getDisplayMedia);
    navigator.mediaDevices?.addEventListener?.('devicechange', refreshDevices);
    loadCallToolbarConfig()
      .then((config) => {
        toolbarConfig = config;
        savedToolbarConfig = config;
      })
      .catch(() => {});

    session = createWebRTCSession({
      conversationId: call.conversationId,
      sessionId: call.sessionId,
      callType: mode,
      direction: call.direction,
      iceTransportPolicy: call.iceTransportPolicy,
      onLocalStream: async (stream) => {
        localStream = stream;
        await tick();
        attachStreams();
        await refreshDevices();
      },
      onRemoteStream: async (stream) => {
        remoteStream = stream;
        await tick();
        attachStreams();
        startTimer();
      },
      onStatus: (nextStatus) => {
        status = nextStatus;
        updateActiveCall({ status: nextStatus });
        if (nextStatus === 'connected') startTimer();
      },
      onRemoteScreenShare: (active) => {
        remoteScreenSharing = active;
      },
      onEnded: (event = {}) =>
        closeActiveCall({
          notifyServer: Boolean(event.notifyServer),
          reason: event.reason || 'connection-ended'
        }),
      onError: ({ message }) => {
        showCallNotice(message, 'error');
      }
    });

    try {
      await session.start();
      if (call.direction === 'incoming' && !acceptCall(call.conversationId)) {
        showCallNotice('Chưa kết nối được máy chủ cuộc gọi.', 'error');
        closeActiveCall({ notifyServer: false });
      }
    } catch {
      closeActiveCall({ notifyServer: true, reason: 'media-error' });
    }
  });

  onDestroy(() => {
    clearInterval(timer);
    navigator.mediaDevices?.removeEventListener?.('devicechange', refreshDevices);
    session?.close();
    if (document.fullscreenElement === root) document.exitFullscreen?.().catch(() => {});
  });

  function attachStreams() {
    if (localVideo && localVideo.srcObject !== localStream) localVideo.srcObject = localStream;
    if (remoteVideo && remoteVideo.srcObject !== remoteStream) remoteVideo.srcObject = remoteStream;
    if (remoteAudio && remoteAudio.srcObject !== remoteStream) remoteAudio.srcObject = remoteStream;
    remoteAudio?.play?.().catch(() => {});
  }

  function startTimer() {
    if (timer) return;
    status = 'connected';
    timer = setInterval(() => (elapsed += 1), 1000);
  }

  async function refreshDevices() {
    if (refreshingDevices) return;
    refreshingDevices = true;
    try {
      const nextDevices = await navigator.mediaDevices.enumerateDevices();
      const outputs = nextDevices.filter(({ kind }) => kind === 'audiooutput');
      const outputIds = new Set(outputs.map(({ deviceId }) => deviceId));
      const activeSink = remoteAudio?.sinkId || selectedSpeaker;
      devices = nextDevices;
      selectedCamera = localStream?.getVideoTracks()[0]?.getSettings()?.deviceId || selectedCamera || '';
      selectedMicrophone = localStream?.getAudioTracks()[0]?.getSettings()?.deviceId || selectedMicrophone || '';
      selectedSpeaker =
        activeSink && (activeSink === 'default' || outputIds.has(activeSink))
          ? activeSink
          : outputs.find(({ deviceId }) => deviceId === 'default')?.deviceId || outputs[0]?.deviceId || 'default';
      showCallNotice('Đã làm mới danh sách thiết bị.', 'success');
    } catch {
      showCallNotice('Không thể làm mới thiết bị lúc này.', 'warning');
    } finally {
      refreshingDevices = false;
    }
  }

  function toggleMic() {
    micOn = !micOn;
    session?.setAudioEnabled(micOn);
  }

  async function toggleCamera() {
    cameraOn = !cameraOn;
    session?.setVideoEnabled(cameraOn);
    await tick();
    attachStreams();
  }

  async function toggleScreenShare() {
    try {
      if (screenSharing) {
        await session?.stopScreenShare();
        screenSharing = false;
      } else {
        screenSharing = await session?.startScreenShare();
      }
    } catch (error) {
      if (error?.name !== 'NotAllowedError') showCallNotice('Không thể chia sẻ màn hình.', 'error');
    }
  }

  async function changeCamera(event) {
    selectedCamera = event.currentTarget.value;
    await session?.replaceInput('video', selectedCamera);
    attachStreams();
  }

  async function changeMicrophone(event) {
    selectedMicrophone = event.currentTarget.value;
    await session?.replaceInput('audio', selectedMicrophone);
  }

  async function changeSpeaker(event) {
    selectedSpeaker = event.currentTarget.value;
    if (remoteAudio?.setSinkId) {
      await remoteAudio.setSinkId(selectedSpeaker);
    }
  }

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement === root) {
        await document.exitFullscreen();
      } else {
        await root?.requestFullscreen();
      }
    } catch {
      showCallNotice('Trình duyệt không hỗ trợ toàn màn hình.', 'warning');
    }
  }

  async function restoreWindow() {
    minimized = false;
    await tick();
    attachStreams();
  }

  function startLocalDrag(event) {
    if (gridLayout || swappedFrames || event.target.closest('button')) return;
    const stageRect = videoStage?.getBoundingClientRect();
    const frameRect = event.currentTarget.getBoundingClientRect();
    if (!stageRect) return;

    dragState = {
      offsetX: event.clientX - frameRect.left,
      offsetY: event.clientY - frameRect.top,
      width: frameRect.width,
      height: frameRect.height
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    moveLocalVideo(event);
  }

  function moveLocalVideo(event) {
    if (!dragState || !videoStage) return;
    const stageRect = videoStage.getBoundingClientRect();
    const maxX = Math.max(0, stageRect.width - dragState.width);
    const maxY = Math.max(0, stageRect.height - dragState.height);
    localPosition = {
      x: Math.min(Math.max(event.clientX - stageRect.left - dragState.offsetX, 0), maxX),
      y: Math.min(Math.max(event.clientY - stageRect.top - dragState.offsetY, 0), maxY)
    };
  }

  function stopLocalDrag() {
    dragState = null;
  }

  function toggleFrameSwap() {
    swappedFrames = !swappedFrames;
    gridLayout = false;
    localPosition = null;
  }

  function toggleGridLayout() {
    gridLayout = !gridLayout;
    swappedFrames = false;
    localPosition = null;
  }

  function resetVideoLayout() {
    gridLayout = false;
    swappedFrames = false;
    localPosition = null;
    zoom = 1;
  }

  function updateToolbarConfig(nextConfig) {
    toolbarConfig = normalizeCallToolbarConfig(nextConfig);
  }

  async function saveToolbarSettings() {
    if (savingToolbar || !hasToolbarChanges) return;
    savingToolbar = true;
    try {
      toolbarConfig = await saveCallToolbarConfig(toolbarConfig);
      savedToolbarConfig = toolbarConfig;
      showCallNotice('Đã lưu thanh điều khiển cuộc gọi.', 'success');
    } catch {
      showCallNotice('Không thể lưu thanh điều khiển.', 'error');
    } finally {
      savingToolbar = false;
    }
  }

  async function resetToolbarSettings() {
    if (savingToolbar || !hasToolbarChanges) return;
    savingToolbar = true;
    try {
      toolbarConfig = await resetCallToolbarConfig();
      savedToolbarConfig = toolbarConfig;
      showCallNotice('Đã khôi phục thanh điều khiển mặc định.', 'success');
    } catch {
      showCallNotice('Không thể khôi phục thiết lập mặc định.', 'error');
    } finally {
      savingToolbar = false;
    }
  }

  function endCurrentCall() {
    closeActiveCall({ notifyServer: true, reason: 'ended' });
    onClose?.();
  }

  function getStatusText(value) {
    const labels = {
      ringing: 'Đang đổ chuông...',
      'requesting-media': 'Đang chuẩn bị thiết bị...',
      connecting: 'Đang kết nối...',
      connected: 'Đã kết nối',
      reconnecting: 'Đang kết nối lại...',
      disconnected: 'Kết nối gián đoạn',
      failed: 'Kết nối thất bại'
    };
    return labels[value] || 'Đang kết nối...';
  }
</script>

<audio bind:this={remoteAudio} autoplay></audio>

{#if minimized}
  <section class="mini-call">
    <div class="mini-avatar">{title.charAt(0).toUpperCase()}</div>
    <div class="min-w-0 flex-1">
      <strong>{title}</strong>
      <span>{status === 'connected' ? formatDurations(elapsed) : statusText}</span>
    </div>
    <button class="mini-button" aria-label="Mở rộng cuộc gọi" onclick={restoreWindow}><ChevronUp size={18} /></button>
    <button class="mini-button danger" aria-label="Kết thúc cuộc gọi" onclick={endCurrentCall}>
      <PhoneOff size={18} />
    </button>
  </section>
{:else}
  <div class="call-backdrop">
    <section bind:this={root} class:expanded class="call-window {mode === 'audio' ? 'audio-mode' : ''}">
      <header class="call-header">
        <div class="call-identity">
          <div class="identity-avatar">{title.charAt(0).toUpperCase()}</div>
          <div class="min-w-0">
            <h2>{title}</h2>
            <p>
              <span class:connected={status === 'connected'}></span>
              {status === 'connected' ? formatDurations(elapsed) : statusText}
            </p>
          </div>
        </div>

        <div class="header-actions">
          <button aria-label="Thu nhỏ cuộc gọi" title="Thu nhỏ" onclick={() => (minimized = true)}>
            <Minimize2 size={17} />
          </button>
          <button aria-label="Đổi kích thước cửa sổ" title="Phóng to/thu nhỏ" onclick={() => (expanded = !expanded)}>
            {#if expanded}<Shrink size={17} />{:else}<Maximize2 size={17} />{/if}
          </button>
          <button aria-label="Toàn màn hình" title="Toàn màn hình" onclick={toggleFullscreen}>
            <Expand size={17} />
          </button>
        </div>
      </header>

      {#if mode === 'video'}
        <main
          bind:this={videoStage}
          class:grid-layout={gridLayout}
          class:swapped={swappedFrames}
          class="video-stage"
          style="--video-zoom:{zoom}">
          <div class="remote-frame">
            {#if remoteStream}
              <video bind:this={remoteVideo} autoplay muted playsinline class="remote-video"></video>
            {:else}
              <div class="waiting-state">
                <div class="waiting-avatar">{title.charAt(0).toUpperCase()}</div>
                <strong>{statusText}</strong>
                <span>Cuộc gọi sẽ bắt đầu khi người nhận trả lời.</span>
              </div>
            {/if}
            {#if remoteScreenSharing}<span class="screen-label">Đang trình bày màn hình</span>{/if}
          </div>

          <div
            class:dragging={Boolean(dragState)}
            class="local-frame"
            style={localPosition && !gridLayout && !swappedFrames
              ? `left:${localPosition.x}px;top:${localPosition.y}px;right:auto;bottom:auto`
              : ''}
            onpointerdown={startLocalDrag}
            onpointermove={moveLocalVideo}
            onpointerup={stopLocalDrag}
            onpointercancel={stopLocalDrag}>
            {#if cameraOn && localStream}
              <video bind:this={localVideo} autoplay muted playsinline></video>
            {:else}
              <div class="camera-off">
                <UserRound size={28} />
                <span>Camera đang tắt</span>
              </div>
            {/if}
            <span class="you-label">Bạn{screenSharing ? ' · đang chia sẻ' : ''}</span>
            <div class="frame-actions">
              <span class="drag-handle" title="Giữ và kéo để di chuyển khung hình"><Move size={13} /></span>
              <button title="Hoán đổi khung hình" aria-label="Hoán đổi khung hình" onclick={toggleFrameSwap}>
                <Repeat2 size={13} />
              </button>
            </div>
          </div>
        </main>
      {:else}
        <main class="audio-stage">
          <div class="audio-rings">
            <div></div>
            <div></div>
            <div></div>
          </div>
          <div class="audio-avatar">{title.charAt(0).toUpperCase()}</div>
          <h2>{title}</h2>
          <p>{status === 'connected' ? formatDurations(elapsed) : statusText}</p>
        </main>
      {/if}

      {#if showSettings}
        <aside class="settings-panel">
          <div class="settings-title">
            <div>
              <strong>Cài đặt cuộc gọi</strong>
              <span>Theo dõi và điều chỉnh ngay trong cuộc gọi</span>
            </div>
            <button
              class:busy={refreshingDevices}
              title={refreshingDevices ? 'Đang làm mới thiết bị...' : 'Làm mới thiết bị'}
              aria-label={refreshingDevices ? 'Đang làm mới thiết bị' : 'Làm mới thiết bị'}
              disabled={refreshingDevices}
              onclick={refreshDevices}>
              <RefreshCw size={15} />
            </button>
          </div>

          <div class="settings-scroll">
            <nav class="settings-tabs" aria-label="Nhóm cài đặt cuộc gọi">
              <button class:active={settingsTab === 'devices'} onclick={() => (settingsTab = 'devices')}>
                Thiết bị
              </button>
              <button class:active={settingsTab === 'network'} onclick={() => (settingsTab = 'network')}>Mạng</button>
              <button class:active={settingsTab === 'display'} onclick={() => (settingsTab = 'display')}>
                Hiển thị
              </button>
              <button class:active={settingsTab === 'toolbar'} onclick={() => (settingsTab = 'toolbar')}>
                Thanh công cụ
              </button>
            </nav>

            <div class="settings-content">
              {#if settingsTab === 'devices'}
                <label for="call-microphone">Microphone</label>
                <select id="call-microphone" value={selectedMicrophone} onchange={changeMicrophone}>
                  {#each microphoneDevices as device, index}
                    <option value={device.deviceId}>{device.label || `Microphone ${index + 1}`}</option>
                  {/each}
                </select>

                {#if remoteAudio?.setSinkId}
                  <label for="call-speaker">Loa</label>
                  <select id="call-speaker" value={selectedSpeaker} onchange={changeSpeaker}>
                    {#if !speakerDevices.some(({ deviceId }) => deviceId === 'default')}
                      <option value="default">Thiết bị âm thanh mặc định</option>
                    {/if}
                    {#each speakerDevices as device, index}
                      <option value={device.deviceId}>
                        {device.label ||
                          (device.deviceId === 'default' ? 'Thiết bị âm thanh mặc định' : `Loa ${index + 1}`)}
                      </option>
                    {/each}
                  </select>
                {:else}
                  <div class="device-note">
                    Trình duyệt đang dùng loa mặc định của hệ thống và không hỗ trợ đổi trực tiếp.
                  </div>
                {/if}

                {#if mode === 'video'}
                  <label for="call-camera">Camera</label>
                  <select id="call-camera" value={selectedCamera} onchange={changeCamera}>
                    {#each cameraDevices as device, index}
                      <option value={device.deviceId}>{device.label || `Camera ${index + 1}`}</option>
                    {/each}
                  </select>
                {/if}
              {:else if settingsTab === 'network'}
                <CallNetworkDiagnostics {session} callStatus={status} />
              {:else if settingsTab === 'display'}
                {#if mode === 'video'}
                  <label for="call-zoom">Độ phóng video từ xa</label>
                  <input id="call-zoom" type="range" min="1" max="1.8" step="0.05" bind:value={zoom} />
                  <div class="display-actions">
                    <button class:active={gridLayout} onclick={toggleGridLayout}>
                      <Grid2X2 size={15} />
                      <span>Chia đôi khung</span>
                    </button>
                    <button class:active={swappedFrames} onclick={toggleFrameSwap}>
                      <Repeat2 size={15} />
                      <span>Hoán đổi video</span>
                    </button>
                    <button onclick={resetVideoLayout}>
                      <RefreshCw size={15} />
                      <span>Đặt lại bố cục</span>
                    </button>
                  </div>
                  <p class="settings-help">
                    Giữ và kéo khung video của bạn để đặt ở vị trí thuận tiện. Chọn hoán đổi để đưa camera của bạn lên
                    khung chính.
                  </p>
                {:else}
                  <p class="settings-help">
                    Cuộc gọi thoại không có tùy chọn bố cục video. Thông tin mạng vẫn có thể theo dõi trong tab Mạng.
                  </p>
                {/if}
              {:else}
                <CallToolbarSettings
                  config={toolbarConfig}
                  {mode}
                  saving={savingToolbar}
                  resetting={savingToolbar}
                  canSave={hasToolbarChanges}
                  canReset={hasToolbarChanges}
                  onChange={updateToolbarConfig}
                  onSave={saveToolbarSettings}
                  onReset={resetToolbarSettings} />
              {/if}
            </div>
          </div>
        </aside>
      {/if}

      <footer
        class:toolbar-compact={toolbarConfig.style === 'compact'}
        class:toolbar-glass={toolbarConfig.style === 'glass'}
        class="call-controls">
        {#each visibleToolbarActions as action (action.id)}
          {#if action.id === 'microphone'}
            <button
              class:off={!micOn}
              class="control-button"
              aria-label={micOn ? 'Tắt microphone' : 'Bật microphone'}
              onclick={toggleMic}>
              {#if micOn}<Mic size={21} />{:else}<MicOff size={21} />{/if}
              <span>Micro</span>
            </button>
          {:else if action.id === 'camera'}
            <button
              class:off={!cameraOn}
              class="control-button"
              aria-label={cameraOn ? 'Tắt camera' : 'Bật camera'}
              onclick={toggleCamera}>
              {#if cameraOn}<Camera size={21} />{:else}<CameraOff size={21} />{/if}
              <span>Camera</span>
            </button>
          {:else if action.id === 'shareScreen'}
            <button
              class:active={screenSharing}
              class="control-button"
              aria-label="Chia sẻ màn hình"
              title={canShareScreen ? 'Chia sẻ màn hình' : 'Thiết bị này không hỗ trợ chia sẻ màn hình'}
              disabled={!canShareScreen}
              onclick={toggleScreenShare}>
              <MonitorUp size={21} />
              <span>Chia sẻ</span>
            </button>
          {:else if action.id === 'layout'}
            <button
              class:active={gridLayout}
              class="control-button desktop-control"
              aria-label="Đổi bố cục"
              onclick={toggleGridLayout}>
              <Grid2X2 size={21} />
              <span>Bố cục</span>
            </button>
          {:else if action.id === 'settings'}
            <button
              class:active={showSettings}
              class="control-button"
              aria-label="Cài đặt cuộc gọi"
              onclick={() => (showSettings = !showSettings)}>
              <Settings size={21} />
              <span>Cài đặt</span>
            </button>
          {:else if action.id === 'endCall'}
            <button class="end-button" aria-label="Kết thúc cuộc gọi" onclick={endCurrentCall}>
              <PhoneOff size={24} />
              <span>Kết thúc</span>
            </button>
          {:else if action.id === 'minimize'}
            <button
              class="control-button desktop-control"
              aria-label="Thu nhỏ cuộc gọi"
              onclick={() => (minimized = true)}>
              <PictureInPicture2 size={21} />
              <span>Thu nhỏ</span>
            </button>
          {/if}
        {/each}
      </footer>
    </section>
  </div>
{/if}

<style>
  .call-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1050;
    display: grid;
    place-items: center;
    padding: 1rem;
    background: rgba(2, 6, 23, 0.72);
    backdrop-filter: blur(16px);
  }

  .call-window {
    position: relative;
    display: flex;
    flex-direction: column;
    width: min(78rem, 92vw);
    height: min(48rem, 88vh);
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 1.7rem;
    color: white;
    background: #020617;
    box-shadow: 0 40px 100px rgba(2, 6, 23, 0.55);
    transition:
      width 180ms ease,
      height 180ms ease,
      border-radius 180ms ease;
  }

  .call-window.expanded {
    width: calc(100vw - 1.5rem);
    height: calc(100dvh - 1.5rem);
  }

  .call-window.audio-mode {
    width: min(28rem, calc(100vw - 1.5rem));
    height: min(42rem, calc(100dvh - 1.5rem));
    background:
      radial-gradient(circle at 50% 20%, rgba(59, 130, 246, 0.35), transparent 24rem),
      linear-gradient(160deg, #0f172a, #020617);
  }

  .call-window.audio-mode.expanded {
    width: calc(100vw - 1.5rem);
    height: calc(100dvh - 1.5rem);
  }

  .call-header {
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 4.2rem;
    padding: 0.65rem 0.8rem 0.65rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(2, 6, 23, 0.78);
    backdrop-filter: blur(20px);
  }

  .call-identity {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    min-width: 0;
  }

  .identity-avatar {
    display: grid;
    place-items: center;
    width: 2.45rem;
    height: 2.45rem;
    border-radius: 0.9rem;
    background: linear-gradient(145deg, #60a5fa, #4f46e5);
    font-weight: 700;
  }

  .call-identity h2 {
    overflow: hidden;
    font-size: 0.88rem;
    font-weight: 650;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .call-identity p {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: #94a3b8;
    font-size: 0.66rem;
  }

  .call-identity p span {
    width: 0.38rem;
    height: 0.38rem;
    border-radius: 999px;
    background: #f59e0b;
  }

  .call-identity p span.connected {
    background: #22c55e;
  }

  .header-actions {
    display: flex;
    gap: 0.3rem;
  }

  .header-actions button,
  .settings-title button {
    display: grid;
    place-items: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 999px;
    color: #cbd5e1;
    background: rgba(255, 255, 255, 0.08);
  }

  .settings-title button.busy {
    color: #93c5fd;
    background: rgba(59, 130, 246, 0.18);
    animation: pulse-soft 0.9s ease-in-out infinite;
  }

  .video-stage {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: #020617;
  }

  .remote-frame {
    position: absolute;
    inset: 0;
    overflow: hidden;
  }

  .remote-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: scale(var(--video-zoom));
    transition: transform 180ms ease;
  }

  .local-frame {
    position: absolute;
    z-index: 10;
    right: 1rem;
    bottom: 1rem;
    width: min(22vw, 15rem);
    aspect-ratio: 16 / 10;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 1rem;
    background: #0f172a;
    box-shadow: 0 20px 44px rgba(0, 0, 0, 0.42);
    cursor: grab;
    touch-action: none;
    user-select: none;
    transition:
      width 180ms ease,
      height 180ms ease,
      border-radius 180ms ease,
      box-shadow 180ms ease;
  }

  .local-frame.dragging {
    cursor: grabbing;
    box-shadow: 0 26px 60px rgba(0, 0, 0, 0.58);
  }

  .local-frame video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: scaleX(-1);
  }

  .video-stage.grid-layout {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    padding: 0.5rem;
  }

  .video-stage.grid-layout .remote-frame,
  .video-stage.grid-layout .local-frame {
    position: relative;
    inset: auto;
    width: auto;
    height: 100%;
    aspect-ratio: auto;
    border-radius: 1rem;
  }

  .video-stage.grid-layout .local-frame {
    cursor: default;
    touch-action: auto;
  }

  .video-stage.swapped .local-frame {
    inset: 0;
    width: 100%;
    height: 100%;
    aspect-ratio: auto;
    border: 0;
    border-radius: 0;
    cursor: default;
    touch-action: auto;
  }

  .video-stage.swapped .remote-frame {
    z-index: 20;
    inset: auto 1rem 1rem auto;
    width: min(22vw, 15rem);
    aspect-ratio: 16 / 10;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 1rem;
    background: #0f172a;
    box-shadow: 0 20px 44px rgba(0, 0, 0, 0.42);
  }

  .video-stage.swapped .remote-video {
    transform: scale(var(--video-zoom));
  }

  .frame-actions {
    position: absolute;
    top: 0.42rem;
    right: 0.42rem;
    display: flex;
    gap: 0.25rem;
    opacity: 0;
    transition: opacity 140ms ease;
  }

  .local-frame:hover .frame-actions,
  .local-frame:focus-within .frame-actions {
    opacity: 1;
  }

  .frame-actions button,
  .drag-handle {
    display: grid;
    place-items: center;
    width: 1.75rem;
    height: 1.75rem;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 999px;
    color: #e2e8f0;
    background: rgba(2, 6, 23, 0.62);
    backdrop-filter: blur(12px);
  }

  .drag-handle {
    cursor: grab;
  }

  .waiting-state,
  .camera-off {
    display: grid;
    place-items: center;
    align-content: center;
    width: 100%;
    height: 100%;
    color: #94a3b8;
    text-align: center;
  }

  .waiting-state span,
  .camera-off span {
    margin-top: 0.35rem;
    font-size: 0.7rem;
  }

  .waiting-avatar,
  .audio-avatar {
    display: grid;
    place-items: center;
    width: 7rem;
    height: 7rem;
    margin-bottom: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 2.3rem;
    background: linear-gradient(145deg, #60a5fa, #4f46e5);
    box-shadow: 0 30px 70px rgba(59, 130, 246, 0.3);
    font-size: 2.8rem;
    font-weight: 700;
  }

  .you-label,
  .screen-label {
    position: absolute;
    bottom: 0.45rem;
    left: 0.45rem;
    padding: 0.25rem 0.45rem;
    border-radius: 999px;
    background: rgba(2, 6, 23, 0.62);
    backdrop-filter: blur(12px);
    font-size: 0.58rem;
  }

  .screen-label {
    top: 0.7rem;
    bottom: auto;
    left: 0.7rem;
    color: #bbf7d0;
  }

  .audio-stage {
    position: relative;
    display: grid;
    place-items: center;
    align-content: center;
    flex: 1;
    overflow: hidden;
  }

  .audio-stage h2 {
    font-size: 1.3rem;
    font-weight: 700;
  }

  .audio-stage p {
    margin-top: 0.3rem;
    color: #94a3b8;
    font-size: 0.78rem;
  }

  .audio-rings {
    position: absolute;
    inset: 50%;
  }

  .audio-rings div {
    position: absolute;
    width: 10rem;
    height: 10rem;
    transform: translate(-50%, -50%);
    border: 1px solid rgba(96, 165, 250, 0.18);
    border-radius: 999px;
    animation: ring 2.4s infinite ease-out;
  }

  .audio-rings div:nth-child(2) {
    animation-delay: 0.8s;
  }
  .audio-rings div:nth-child(3) {
    animation-delay: 1.6s;
  }

  .settings-panel {
    position: absolute;
    z-index: 30;
    right: 1rem;
    top: 4.8rem;
    width: min(24rem, calc(100% - 2rem));
    max-height: calc(100% - 10.8rem);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 1.2rem;
    background: rgba(15, 23, 42, 0.9);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.38);
    backdrop-filter: blur(24px);
    scrollbar-width: thin;
    scrollbar-color: rgba(96, 165, 250, 0.55) rgba(15, 23, 42, 0.28);
    scrollbar-gutter: stable;
  }

  .settings-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    max-height: none;
    margin-right: -1.5rem;
    padding-right: 0.7rem;
    border-radius: 1rem;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: rgba(96, 165, 250, 0.55) rgba(15, 23, 42, 0.28);
    scrollbar-gutter: stable;
  }

  .settings-scroll::-webkit-scrollbar {
    width: 0.46rem;
  }

  .settings-scroll::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 999px;
  }

  .settings-scroll::-webkit-scrollbar-thumb {
    border: 0.08rem solid transparent;
    background: linear-gradient(180deg, rgba(147, 197, 253, 0.96), rgba(59, 130, 246, 0.96));
    border-radius: 999px;
    background-clip: padding-box;
  }

  .settings-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.8rem;
    font-size: 0.8rem;
  }

  .settings-title span {
    display: block;
    margin-top: 0.12rem;
    color: #94a3b8;
    font-size: 0.54rem;
    font-weight: 400;
  }

  .settings-tabs {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.3rem;
    margin-bottom: 0.75rem;
    padding: 0.25rem;
    border-radius: 0.8rem;
    background: rgba(2, 6, 23, 0.44);
  }

  .settings-tabs button {
    padding: 0.48rem 0.35rem;
    border-radius: 0.62rem;
    color: #94a3b8;
    font-size: 0.58rem;
    font-weight: 700;
  }

  .settings-tabs button.active {
    color: #eff6ff;
    background: rgba(59, 130, 246, 0.28);
    box-shadow: inset 0 0 0 1px rgba(96, 165, 250, 0.18);
  }

  .settings-content {
    min-height: 12rem;
  }

  .settings-panel label {
    display: block;
    margin: 0.7rem 0 0.3rem;
    color: #94a3b8;
    font-size: 0.63rem;
  }

  .settings-panel select,
  .settings-panel input {
    width: 100%;
  }

  .settings-panel select {
    padding: 0.55rem 0.65rem;
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 0.7rem;
    color: white;
    background: #1e293b;
    font-size: 0.7rem;
  }

  .device-note {
    margin-top: 0.7rem;
    padding: 0.65rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 0.7rem;
    color: #94a3b8;
    background: rgba(2, 6, 23, 0.28);
    font-size: 0.56rem;
    line-height: 1.5;
  }

  @keyframes pulse-soft {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(0.96);
    }
  }

  .display-actions {
    display: grid;
    gap: 0.45rem;
    margin-top: 0.85rem;
  }

  .display-actions button {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.65rem 0.7rem;
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 0.75rem;
    color: #cbd5e1;
    background: rgba(255, 255, 255, 0.045);
    font-size: 0.63rem;
    font-weight: 650;
  }

  .display-actions button.active {
    border-color: rgba(96, 165, 250, 0.3);
    color: #dbeafe;
    background: rgba(37, 99, 235, 0.2);
  }

  .settings-help {
    margin-top: 0.75rem;
    padding: 0.65rem;
    border-radius: 0.75rem;
    color: #94a3b8;
    background: rgba(2, 6, 23, 0.32);
    font-size: 0.56rem;
    line-height: 1.55;
  }

  .call-controls {
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: clamp(0.35rem, 1.5vw, 0.75rem);
    min-height: 5.8rem;
    padding: 0.65rem max(0.65rem, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(2, 6, 23, 0.86);
    backdrop-filter: blur(22px);
  }

  .call-controls.toolbar-compact {
    min-height: 4.8rem;
    gap: 0.45rem;
  }

  .call-controls.toolbar-compact .control-button,
  .call-controls.toolbar-compact .end-button {
    width: 3.35rem;
    height: 3.35rem;
    border-radius: 999px;
  }

  .call-controls.toolbar-compact .control-button span,
  .call-controls.toolbar-compact .end-button span {
    display: none;
  }

  .call-controls.toolbar-glass {
    background:
      linear-gradient(180deg, rgba(15, 23, 42, 0.52), rgba(2, 6, 23, 0.72)),
      radial-gradient(circle at 50% 0%, rgba(96, 165, 250, 0.14), transparent 45%);
    backdrop-filter: blur(30px) saturate(1.35);
  }

  .call-controls.toolbar-glass .control-button {
    border: 1px solid rgba(255, 255, 255, 0.11);
    background: rgba(255, 255, 255, 0.07);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  .control-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    width: 4.2rem;
    height: 3.8rem;
    border-radius: 1rem;
    color: #e2e8f0;
    background: rgba(255, 255, 255, 0.08);
  }

  .control-button span {
    font-size: 0.55rem;
  }

  .control-button.off {
    color: #fecaca;
    background: rgba(239, 68, 68, 0.28);
  }

  .control-button.active {
    color: #bfdbfe;
    background: rgba(37, 99, 235, 0.45);
  }

  .control-button:disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }

  .end-button {
    display: grid;
    place-items: center;
    width: 4rem;
    height: 3.2rem;
    border-radius: 1.2rem;
    color: white;
    background: #ef4444;
    box-shadow: 0 12px 30px rgba(239, 68, 68, 0.32);
  }

  .end-button span {
    display: none;
  }

  .mini-call {
    position: fixed;
    z-index: 1100;
    right: 1rem;
    bottom: max(1rem, env(safe-area-inset-bottom));
    display: flex;
    align-items: center;
    gap: 0.65rem;
    width: min(24rem, calc(100vw - 2rem));
    padding: 0.65rem;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 1.2rem;
    color: white;
    background: rgba(15, 23, 42, 0.94);
    box-shadow: 0 24px 60px rgba(2, 6, 23, 0.35);
    backdrop-filter: blur(22px);
  }

  .mini-avatar {
    display: grid;
    place-items: center;
    width: 2.7rem;
    height: 2.7rem;
    border-radius: 0.9rem;
    background: linear-gradient(145deg, #60a5fa, #4f46e5);
    font-weight: 700;
  }

  .mini-call strong,
  .mini-call span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mini-call strong {
    font-size: 0.78rem;
  }
  .mini-call span {
    color: #94a3b8;
    font-size: 0.62rem;
  }

  .mini-button {
    display: grid;
    place-items: center;
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
  }

  .mini-button.danger {
    background: #ef4444;
  }

  @keyframes ring {
    from {
      transform: translate(-50%, -50%) scale(0.7);
      opacity: 0.8;
    }
    to {
      transform: translate(-50%, -50%) scale(2.2);
      opacity: 0;
    }
  }

  @media (max-width: 720px) {
    .call-backdrop {
      padding: 0;
    }
    .call-window,
    .call-window.expanded,
    .call-window.audio-mode {
      width: 100vw;
      height: 100dvh;
      border: 0;
      border-radius: 0;
    }

    .call-header {
      padding-top: max(0.65rem, env(safe-area-inset-top));
    }
    .header-actions button:nth-child(2) {
      display: none;
    }
    .local-frame {
      width: 7.5rem;
      right: 0.65rem;
      bottom: 0.65rem;
    }
    .frame-actions {
      opacity: 1;
    }
    .desktop-control {
      display: none;
    }
    .control-button {
      width: 3.25rem;
      height: 3.25rem;
      border-radius: 999px;
    }
    .control-button span {
      display: none;
    }
    .end-button {
      width: 3.6rem;
      height: 3.25rem;
      border-radius: 999px;
    }
    .call-controls {
      min-height: 5rem;
    }
    .settings-panel {
      top: calc(max(0.65rem, env(safe-area-inset-top)) + 4.2rem);
      right: 0.65rem;
      width: calc(100% - 1.3rem);
      max-height: calc(100% - 10.2rem - env(safe-area-inset-bottom));
    }
    .settings-tabs {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .video-stage.grid-layout {
      display: block;
      padding: 0;
    }
    .video-stage.grid-layout .remote-frame {
      position: absolute;
      inset: 0;
    }
    .video-stage.grid-layout .local-frame {
      position: absolute;
      inset: auto 0.65rem 0.65rem auto;
      width: 7.5rem;
      height: auto;
      aspect-ratio: 10 / 14;
    }
    .video-stage.swapped .remote-frame {
      inset: auto 0.65rem 0.65rem auto;
      width: 7.5rem;
      aspect-ratio: 10 / 14;
    }
  }
</style>
